import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import { DispatchScheduleTab } from './DispatchScheduleTab';
import { getErrorMessage } from '@/utils/errorUtils';
import { useBroadcastSync, SYNC_TYPES } from '@/hooks/useBroadcastSync';
import { useWebSocketSubscribe } from '@/context/WebSocketContext';
import { useUnsavedChanges, buildConfirmClose } from '@/hooks/useUnsavedChanges';
import { ShipmentFeesSection } from '@/components/ShipmentFeesSection';

// Import shared form section components
import {
  LoadContainerInfoSection,
  LoadTerminalChassisSection,
  LoadDatesSection,
  LoadFeesSection,
  LoadOptionsRemarksSection,
  TerminalAvailabilitySection
} from './components';

// Extracted edit-sheet sub-components (Tabs + Dialogs).
import {
  StatusHistoryTab,
  EventLogsTab,
  LoadDocumentsTab,
  StatusChangeDialog,
  DeleteConfirmDialog,
  FullPreviewModal,
  AddNewAddressDialog,
} from './components/editload';

// ============================================================================
// CONSTANTS
// ============================================================================

const AUTO_DOC_TYPE = '__auto__';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const EditLoadSheet = ({ open, onOpenChange, shipment, masterData, customers = [], onSuccess }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  // Unified container timeline state (drives the new section inside the Event Logs tab)
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [formData, setFormData] = useState({});
  const [aiBadges, setAiBadges] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pullingTicket, setPullingTicket] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(AUTO_DOC_TYPE);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [savingTypeId, setSavingTypeId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Customer & Address state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Inline "+ Add new address" dialog state (let dispatchers create a new
  // address without leaving the load — saves a trip to Master Tables)
  const [addNewAddressOpen, setAddNewAddressOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: '', address: '', city: '', state: '', zip_code: '', contact_name: '', contact_phone: '' });
  const [savingNewAddress, setSavingNewAddress] = useState(false);
  
  // Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusDate, setStatusDate] = useState(new Date().toISOString().split('T')[0]);  // Default to today
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('shipment-status');

  // Track unsaved edits — the "● Unsaved" pill + close-confirm prompt both
  // read from this single source of truth. We only arm tracking once the
  // sheet has actually opened with a shipment loaded (avoids false positives
  // during the initial render before `setFormData(shipmentCopy)` fires).
  const trackingReady = !!shipment && open;
  const isDirty = useUnsavedChanges(formData, trackingReady);
  const handleConfirmClose = buildConfirmClose(isDirty, () => onOpenChange(false));

  // Preview states
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const previousPreviewUrl = useRef(null);

  // Helper to safely parse dates - handles invalid/malformed date strings
  const safeParseDateValue = (dateValue) => {
    if (!dateValue) return null;
    
    // Handle empty strings and invalid string values
    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (!trimmed || trimmed.toLowerCase() === 'none' || trimmed.toLowerCase() === 'null' || trimmed === '-') {
        return null;
      }
    }
    
    try {
      const date = new Date(dateValue);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date value: ${dateValue}`);
        return null;
      }
      return date;
    } catch (e) {
      console.warn(`Error parsing date value: ${dateValue}`, e);
      return null;
    }
  };

  // Initialize form data, badges, and documents whenever a new shipment is
  // loaded into the sheet (i.e., its id changes) or the sheet is reopened.
  // We intentionally depend on `shipment?.id` and `open` instead of the full
  // shipment object so that:
  //   1. Reopening the same shipment refreshes form state from the latest props
  //   2. Unrelated parent re-renders (e.g., WebSocket list refreshes producing
  //      a new object reference for the same shipment) do NOT clobber the
  //      dispatcher's in-progress edits.
  // Customer matching is handled in a separate effect below so we don't
  // overwrite edits when `customers` finally loads.
  useEffect(() => {
    if (!shipment) return;
    const shipmentCopy = { ...shipment };

    const dateFields = ['eta', 'last_free_date', 'lfd', 'pickup_date', 'delivery_date', 'appointment_date', 'mty_return', 'mty_rdy', 'lfd_yard'];
    dateFields.forEach(field => {
      shipmentCopy[field] = safeParseDateValue(shipment[field]);
    });

    setFormData(shipmentCopy);
    setAiBadges(shipment.ai_autofilled_fields || {});
    const normalizedDocs = (shipment.documents || []).map(doc => ({
      ...doc,
      type: doc.type || doc.document_type || 'OTHER'
    }));
    setDocuments(normalizedDocs);
    fetchStatusHistory();
    fetchEventLogs();
    fetchContainerTimeline();
  }, [shipment?.id, open]);

  // Resolve the selected customer asynchronously without stomping over the
  // form's own state. Runs whenever the customer_id changes (e.g., user picks
  // a new one) or the customers list finishes loading.
  useEffect(() => {
    if (formData.customer_id && customers.length > 0) {
      const customer = customers.find(c => c.id === formData.customer_id);
      setSelectedCustomer(customer || null);
    } else {
      setSelectedCustomer(null);
    }
  }, [formData.customer_id, customers]);

  // Cleanup previous blob URLs to prevent memory leaks
  // Use a slight delay before revoking to ensure the iframe/img has loaded the content
  useEffect(() => {
    const previousUrl = previousPreviewUrl.current;
    previousPreviewUrl.current = previewUrl;
    
    // Schedule cleanup of the previous URL (with delay to ensure content loaded)
    let timeoutId;
    if (previousUrl && previousUrl !== previewUrl && previousUrl.startsWith('blob:')) {
      timeoutId = setTimeout(() => {
        window.URL.revokeObjectURL(previousUrl);
      }, 1000); // 1 second delay
    }
    
    // Cleanup on unmount
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      // On unmount, revoke the current URL immediately
      if (previewUrl && previewUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const fetchStatusHistory = async () => {
    try {
      const response = await api.get(`/shipments/${shipment.id}/history`);
      setStatusHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch status history');
    }
  };

  const fetchEventLogs = async () => {
    try {
      // Fetch dispatch logs for this shipment from the new endpoint
      const response = await api.get(`/dispatches/logs/shipment/${shipment.id}`);
      setEventLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event logs:', error);
      setEventLogs([]);
    }
  };

  // Fetch the unified container timeline (drives the in-load Timeline section).
  // Same endpoint that powers the right-click → Timeline dialog.
  // Debounced because multiple sibling actions (save, doc upload, dispatch refresh)
  // call this in rapid succession — we only need one fetch per burst.
  const timelineFetchTimer = useRef(null);
  // Cancel any pending debounced fetch when the sheet unmounts so we don't fire
  // a stale request after the user closes the drawer.
  useEffect(() => () => {
    if (timelineFetchTimer.current) clearTimeout(timelineFetchTimer.current);
  }, []);
  const fetchContainerTimeline = useCallback(() => {
    if (!shipment?.id) return;
    if (timelineFetchTimer.current) clearTimeout(timelineFetchTimer.current);
    timelineFetchTimer.current = setTimeout(async () => {
      setTimelineLoading(true);
      setTimelineError(null);
      try {
        const response = await api.get(`/shipments/${shipment.id}/timeline`);
        setTimelineEvents(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setTimelineError(e?.response?.data?.detail || 'Failed to load timeline');
        setTimelineEvents([]);
      } finally {
        setTimelineLoading(false);
      }
    }, 250);
  }, [shipment?.id, api]);

  // ---- Live timeline updates ----
  // (a) Cross-tab: another tab in this browser edits this shipment → refresh.
  // (b) Server-pushed WebSocket: another user adds a comment / dispatch
  //     fires / updates this shipment → refresh. Both flow through the same
  //     debounced fetchContainerTimeline, so the freshly-arrived event
  //     animates in via the amber pulse in ContainerTimelineList.
  useBroadcastSync(useCallback((msg) => {
    if (!shipment?.id || !msg) return;
    if (msg.type !== SYNC_TYPES.SHIPMENT_UPDATED) return;
    if (msg.payload?.shipmentId !== shipment.id) return;
    fetchContainerTimeline();
  }, [shipment?.id, fetchContainerTimeline]));

  useWebSocketSubscribe(useCallback((data) => {
    if (!shipment?.id || !data?.type) return;
    // The server emits two payload shapes depending on the route:
    //   - flat:   { type:'comment_added', shipment_id:'...' }
    //   - nested: { type:'shipment_updated', shipment:{ id:'...', ... } }
    const sameShipment =
      data.shipment_id === shipment.id
      || data.shipment?.id === shipment.id
      || data.dispatch?.shipment_id === shipment.id
      || data.message?.shipment_id === shipment.id;
    if (!sameShipment) return;
    const interesting = [
      'comment_added', 'comment_updated', 'comment_deleted',
      'new_dispatch', 'dispatch_response',
      'shipment_updated', 'status_updated',
    ];
    if (interesting.includes(data.type)) {
      fetchContainerTimeline();
      if (data.type === 'new_dispatch' || data.type === 'dispatch_response') {
        fetchEventLogs();
      }
    }
  }, [shipment?.id, fetchContainerTimeline]));

  const handleUploadDocument = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingDoc(true);
    const uploadedDocs = [];
    const failedUploads = [];
    let aiHits = 0;
    let filenameHits = 0;
    let aggregateAutofill = {};
    
    try {
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formDataUpload = new FormData();
          formDataUpload.append('file', file);
          formDataUpload.append('document_type', selectedDocType);
          const response = await api.post(`/shipments/${shipment.id}/documents`, formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } });
          const src = response.data.detection_source;
          if (src === 'ai') aiHits += 1;
          if (src === 'filename') filenameHits += 1;
          const af = response.data.autofilled || {};
          aggregateAutofill = { ...aggregateAutofill, ...af };
          const newDoc = {
            id: response.data.id,
            filename: response.data.filename,
            type: response.data.document_type || selectedDocType,
            document_type: response.data.document_type,
            detection_source: src,
            uploaded_at: new Date().toISOString(),
            aiDetected: response.data.ai_detected || false,
          };
          uploadedDocs.push(newDoc);
        } catch (error) {
          failedUploads.push(file.name);
        }
      }
      
      // Update state with all successfully uploaded docs
      if (uploadedDocs.length > 0) {
        setDocuments(prev => [...prev, ...uploadedDocs]);
        fetchEventLogs();
        fetchContainerTimeline();
        const total = uploadedDocs.length;
        if (aiHits > 0 || filenameHits > 0) {
          const parts = [];
          if (aiHits) parts.push(`${aiHits} AI`);
          if (filenameHits) parts.push(`${filenameHits} filename`);
          toast.success(`${total} doc(s) uploaded — auto-detected: ${parts.join(', ')}`);
        } else {
          toast.success(`${total} document(s) uploaded`);
        }

        // Surface AI auto-filled load fields (container/terminal/job/chassis/pin/appt) so the
        // dispatcher sees what changed and we refresh the form to show it.
        const filledKeys = Object.keys(aggregateAutofill).filter((k) => k !== 'terminal_name' || aggregateAutofill[k]);
        if (filledKeys.length > 0) {
          const labels = {
            container_number: 'container',
            job_number: 'job #',
            terminal_id: 'terminal',
            terminal: 'terminal',
            terminal_name: 'terminal',
            chassis_number: 'chassis',
            pin: 'PIN',
            appointment_date: 'appt',
            mbl: 'MBL',
            seal_number: 'seal',
          };
          const friendly = Array.from(
            new Set(filledKeys.map((k) => labels[k] || k))
          );
          toast.success(`AI filled: ${friendly.join(', ')}`, { duration: 5000 });
          // Optimistically update local form state
          setFormData((prev) => {
            const next = { ...prev };
            const copyIfEmpty = (key, value) => {
              if (value && !next[key]) next[key] = value;
            };
            copyIfEmpty('container_number', aggregateAutofill.container_number);
            copyIfEmpty('job_number', aggregateAutofill.job_number);
            copyIfEmpty('terminal_id', aggregateAutofill.terminal_id);
            copyIfEmpty('terminal', aggregateAutofill.terminal);
            copyIfEmpty('chassis_number', aggregateAutofill.chassis_number);
            copyIfEmpty('pin', aggregateAutofill.pin);
            copyIfEmpty('appointment_date', aggregateAutofill.appointment_date);
            copyIfEmpty('mbl', aggregateAutofill.mbl);
            copyIfEmpty('seal_number', aggregateAutofill.seal_number);
            return next;
          });
          // Also synthesize ai_autofilled_fields entries so badges show
          // immediately without needing a full refetch. The backend has the
          // authoritative data on the next load.
          const lastFile = files[files.length - 1];
          const synth = {};
          Object.entries(aggregateAutofill).forEach(([k, v]) => {
            const fieldName = k === 'terminal_name' ? 'terminal_id' : k;
            if (!aiBadges[fieldName] && v) {
              synth[fieldName] = {
                source: 'ai',
                confidence: 'high',
                filename: lastFile?.name || 'document',
                at: new Date().toISOString(),
                value: v,
              };
            }
          });
          if (Object.keys(synth).length > 0) {
            setAiBadges((prev) => ({ ...prev, ...synth }));
          }
          // Note: we intentionally do NOT call onSuccess() here. onSuccess()
          // closes the edit sheet (handleLoadUpdated → setEditSheetOpen(false))
          // and we want the dispatcher to stay on the edit sheet to verify
          // the AI-autofilled fields before saving.
        }
      }
      
      if (failedUploads.length > 0) {
        toast.error(`Failed to upload: ${failedUploads.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChangeDocType = async (doc, newType) => {
    if (!newType || newType === (doc.document_type || doc.type)) {
      setEditingTypeId(null);
      return;
    }
    setSavingTypeId(doc.id);
    try {
      await api.patch(`/shipments/${shipment.id}/documents/${doc.id}`, {
        document_type: newType,
      });
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? { ...d, type: newType, document_type: newType, detection_source: 'user' }
            : d,
        ),
      );
      toast.success(`Type set to ${newType}`);
      fetchEventLogs();
      fetchContainerTimeline();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update type');
    } finally {
      setSavingTypeId(null);
      setEditingTypeId(null);
    }
  };

  // Remove a single AI badge after the user has reviewed the field.
  const handleAIDismissed = (field) => {
    setAiBadges((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Bulk-change doc type for many selected docs in parallel
  const handleBulkChangeType = async (docIds, newType) => {
    if (!docIds?.length || !newType) return;
    try {
      const results = await Promise.allSettled(
        docIds.map((id) =>
          api.patch(`/shipments/${shipment.id}/documents/${id}`, {
            document_type: newType,
          }),
        ),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      if (ok > 0) {
        setDocuments((prev) =>
          prev.map((d) =>
            docIds.includes(d.id)
              ? { ...d, type: newType, document_type: newType, detection_source: 'user' }
              : d,
          ),
        );
        toast.success(`Set ${ok} doc${ok > 1 ? 's' : ''} to ${newType}`);
      }
      if (fail > 0) {
        toast.error(`Failed to update ${fail} doc${fail > 1 ? 's' : ''}`);
      }
      fetchEventLogs();
      fetchContainerTimeline();
    } catch (err) {
      toast.error('Bulk update failed');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await api.get(`/shipments/${shipment.id}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      await api.delete(`/shipments/${shipment.id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      fetchEventLogs();
      fetchContainerTimeline(); // Refresh event logs after document deletion
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Pull gate ticket from terminal portal
  const handlePullTicket = async () => {
    setPullingTicket(true);
    try {
      const res = await api.post(`/shipments/${shipment.id}/pull-ticket`);
      if (res.data?.duplicate) {
        toast.info(res.data.message || 'Ticket already attached');
      } else {
        toast.success(res.data?.message || 'Gate ticket pulled and attached!');
        // Refresh documents
        const shipRes = await api.get(`/shipments/${shipment.id}`);
        if (shipRes.data?.documents) {
          setDocuments(shipRes.data.documents.map((d, i) => ({
            ...d, id: d.id || `doc-${i}`, type: d.document_type || d.type
          })));
        }
      }
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to pull ticket';
      toast.error(errMsg);
    } finally {
      setPullingTicket(false);
    }
  };


  const getDocumentPreviewUrl = useCallback(async (doc) => {
    if (!doc) return null;
    try {
      setPreviewLoading(true);
      
      // If document has inline base64 data, create a blob URL (more reliable for PDF rendering)
      if (doc.data) {
        // Convert base64 to blob for better browser compatibility
        const byteCharacters = atob(doc.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        return window.URL.createObjectURL(blob);
      }
      
      // Fallback: fetch from API if no inline data
      if (!shipment?.id) return null;
      const response = await api.get(`/shipments/${shipment.id}/documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: doc.content_type || 'application/octet-stream' });
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to load preview:', error);
      return null;
    } finally {
      setPreviewLoading(false);
    }
  }, [api, shipment?.id]);

  const handlePreviewHover = async (doc) => {
    if (previewDoc?.id === doc.id && previewUrl) return;
    setPreviewDoc(doc);
    const url = await getDocumentPreviewUrl(doc);
    setPreviewUrl(url);
  };

  const handleOpenFullPreview = async (doc) => {
    setPreviewDoc(doc);
    if (!previewUrl || previewDoc?.id !== doc.id) {
      const url = await getDocumentPreviewUrl(doc);
      setPreviewUrl(url);
    }
    setShowFullPreview(true);
  };

  const isPreviewable = (doc) => {
    const type = doc.content_type || '';
    const filename = doc.filename?.toLowerCase() || '';
    return type.startsWith('image/') || type === 'application/pdf' || filename.match(/\.(pdf|png|jpg|jpeg|gif|webp)$/);
  };

  const handleDeleteLoad = async () => {
    setDeleting(true);
    try {
      await api.delete(`/shipments/${shipment.id}`);
      toast.success('Load deleted successfully');
      setDeleteDialogOpen(false);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete load'));
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle customer selection change
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer || null);
    
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        customer_name: customer.name,
        // Clear address fields when customer changes
        address_id: '',
        delivery_address: '',
        delivery_city: '',
        delivery_state: '',
        delivery_zip: '',
        delivery_contact: '',
        delivery_phone: ''
      }));
      
      // Auto-select default address if available
      const defaultAddr = customer.addresses?.find(a => a.is_default);
      if (defaultAddr) {
        handleAddressChange(defaultAddr.id, customer);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        address_id: '',
        delivery_address: '',
        delivery_city: '',
        delivery_state: '',
        delivery_zip: '',
        delivery_contact: '',
        delivery_phone: ''
      }));
    }
  };

  // Handle address selection change
  const handleAddressChange = (addressId, customer = selectedCustomer) => {
    if (!customer) return;
    
    const address = customer.addresses?.find(a => a.id === addressId);
    if (address) {
      setFormData(prev => ({
        ...prev,
        address_id: addressId,
        delivery_address: address.address || '',
        delivery_city: address.city || '',
        delivery_state: address.state || '',
        delivery_zip: address.zip_code || '',
        delivery_contact: address.contact_name || '',
        delivery_phone: address.contact_phone || ''
      }));
    }
  };

  // Save a brand-new address for the currently selected customer, then
  // auto-select it on this shipment so the user doesn't have to re-pick.
  const handleSaveNewAddress = async () => {
    if (!selectedCustomer || !newAddress.address) {
      toast.error('Street address is required');
      return;
    }
    setSavingNewAddress(true);
    try {
      const res = await api.post(
        `/customers/${selectedCustomer.id}/addresses`,
        { ...newAddress, is_default: false }
      );
      // The endpoint returns the WHOLE updated customer (with the new address
      // appended). Pull out the last address for auto-selection.
      const updatedCustomer = res?.data || {};
      const created = (updatedCustomer.addresses || []).slice(-1)[0];
      if (!created?.id) {
        throw new Error('No address returned');
      }
      setSelectedCustomer(updatedCustomer);
      handleAddressChange(created.id, updatedCustomer);
      toast.success('Address added');
      setAddNewAddressOpen(false);
      setNewAddress({ label: '', address: '', city: '', state: '', zip_code: '', contact_name: '', contact_phone: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add address');
    } finally {
      setSavingNewAddress(false);
    }
  };

  // Returns `true` on success, `false` on failure. Save-and-Exit reads the
  // return value to decide whether to close the sheet — keeping it open on
  // failure preserves the user's edits and the error toast.
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const payload = { ...formData };
      delete payload.id;
      delete payload.job_number;
      delete payload.created_at;
      delete payload.created_by;
      
      // Properly handle date fields - convert to ISO string or null
      const dateFields = ['eta', 'last_free_date', 'pickup_date', 'delivery_date', 'appointment_date', 'mty_return', 'mty_rdy', 'lfd_yard'];
      dateFields.forEach(field => {
        const value = payload[field];
        if (value instanceof Date && !isNaN(value.getTime())) {
          // Valid Date object - convert to ISO string
          payload[field] = value.toISOString();
        } else if (typeof value === 'string' && value.trim()) {
          // String value - try to parse it
          const parsed = new Date(value);
          if (!isNaN(parsed.getTime()) && parsed.getFullYear() >= 2000) {
            // Valid parsed date with reasonable year
            payload[field] = parsed.toISOString();
          } else {
            // Invalid or partial date string (like "1/13") - set to null to avoid API error
            // The original value is preserved in the database until user explicitly changes it
            delete payload[field];
          }
        } else if (value === null || value === undefined || value === '') {
          // Remove null/undefined/empty values for date fields
          delete payload[field];
        }
      });
      
      await api.put(`/shipments/${shipment.id}`, payload);
      fetchEventLogs();
      fetchContainerTimeline(); // Refresh event logs after shipment update
      toast.success('Changes saved');
      // Don't call onSuccess() here - Save should NOT close the sheet
      return true;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update load'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const openStatusChangeDialog = (status = '') => {
    setNewStatus(status || formData.status);
    setSelectedDriverId(formData.driver_id || '');
    setStatusMessage('');
    setStatusDate('');
    setStatusDialogOpen(true);
  };

  const handleStatusChange = async () => {
    if (!newStatus) { toast.error('Please select a status'); return; }
    try {
      const driver = masterData.drivers?.find(d => d.id === selectedDriverId);
      const driverName = driver?.name || null;
      
      // Build status update payload
      const statusPayload = { 
        status: newStatus, 
        message: statusMessage || `Status changed to ${newStatus}`, 
        driver_id: selectedDriverId || undefined, 
        driver_name: driverName || undefined,
        date: statusDate || undefined,
        effective_date: statusDate || undefined
      };
      
      await api.post(`/shipments/${shipment.id}/status`, statusPayload);
      
      const updatePayload = {};
      if (selectedDriverId !== formData.driver_id) {
        updatePayload.driver_id = selectedDriverId || null;
        updatePayload.driver_name = driverName || null;
      }
      if (newStatus === 'Container Returned' && !formData.mty_return) {
        updatePayload.mty_return = `${new Date().toISOString().split('T')[0]}T12:00:00`;
      }
      
      // Add date field based on status type
      if (statusDate) {
        if (newStatus === 'Delivered to Customer' || newStatus === 'Delivered to Exam Location') {
          updatePayload.delivery_date = statusDate;
        } else if (newStatus === 'Container Returned') {
          updatePayload.mty_return = statusDate;
        } else if (newStatus === 'Available for Pickup') {
          updatePayload.pickup_date = statusDate;
        } else {
          updatePayload.status_date = statusDate;
        }
      }
      
      if (Object.keys(updatePayload).length > 0) await api.put(`/shipments/${shipment.id}`, updatePayload);
      
      fetchStatusHistory();
      fetchEventLogs();
      fetchContainerTimeline();
      setFormData(prev => ({ 
        ...prev, 
        status: newStatus, 
        driver_id: selectedDriverId, 
        driver_name: driverName, 
        ...(newStatus === 'Container Returned' && !prev.mty_return ? { mty_return: statusDate || `${new Date().toISOString().split('T')[0]}T12:00:00` } : {}),
        ...(statusDate && newStatus === 'Delivered to Customer' ? { delivery_date: statusDate } : {}),
        ...(statusDate && newStatus === 'Available for Pickup' ? { pickup_date: statusDate } : {})
      }));
      setStatusDialogOpen(false);
      toast.success('Status updated');
      // Don't call onSuccess() here - status change should stay on the edit sheet
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteStatusEntry = async (entryId) => {
    try {
      const resp = await api.delete(`/shipments/${shipment.id}/status-history/${entryId}`);
      // iter 254 fix: if the backend rolled the shipment back (because the
      // user deleted the most recent status change), reflect that in the
      // local form state — otherwise the next Save would re-apply the
      // stale "Container Returned" status and undo the rollback.
      const rb = resp.data?.rolled_back;
      if (rb) {
        setFormData((prev) => {
          const next = { ...prev, status: rb.to };
          if (rb.cleared_field && rb.cleared_field in next) {
            next[rb.cleared_field] = '';
          }
          return next;
        });
        toast.success(`Status entry deleted — rolled back to "${rb.to}"`);
      } else {
        toast.success('Status entry deleted');
      }
      fetchStatusHistory();
    } catch (error) {
      toast.error('Failed to delete status entry');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  if (!shipment) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) { handleConfirmClose(); } else { onOpenChange(true); } }}>
        <SheetContent className="w-full sm:max-w-2xl bg-slate-900 border-slate-800 overflow-y-auto p-0" data-testid="edit-load-sheet" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <SheetHeader className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SheetTitle className="font-heading text-white">Edit Load - {shipment.container_number}</SheetTitle>
                {isDirty && (
                  <Badge
                    variant="outline"
                    className="border-amber-500/60 bg-amber-500/10 text-amber-300 text-[10px] font-medium animate-pulse"
                    data-testid="unsaved-indicator"
                  >
                    ● Unsaved
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className="font-mono text-xs">{shipment.job_number}</Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSubmit} disabled={loading} size="sm" className="bg-orange-500 hover:bg-orange-400" data-testid="save-changes-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
              </Button>
              <Button
                onClick={async () => {
                  // Only close on actual success — handleSubmit returns false
                  // if the save errored (toast already fired), so we keep the
                  // sheet open and the user's edits intact.
                  const ok = await handleSubmit();
                  if (ok) {
                    onSuccess?.();
                    onOpenChange(false);
                  }
                }}
                disabled={loading}
                size="sm"
                className="bg-green-600 hover:bg-green-500"
                data-testid="save-and-exit-load-btn"
              >
                Save and Exit
              </Button>
              <Button onClick={() => setDeleteDialogOpen(true)} variant="outline" size="sm" className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white" data-testid="delete-load-btn">
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
              <Button onClick={handleConfirmClose} variant="outline" size="sm" className="border-slate-700" data-testid="exit-load-btn">Exit</Button>
            </div>
          </SheetHeader>

          <Tabs defaultValue="shipment-status" className="w-full" onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start rounded-none border-b border-slate-800 bg-transparent h-auto p-0">
              <TabsTrigger value="shipment-status" className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 data-[state=active]:bg-green-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Shipment Status</TabsTrigger>
              <TabsTrigger value="documents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-500 data-[state=active]:bg-cyan-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Documents ({documents.length})</TabsTrigger>
              <TabsTrigger value="dispatch-schedule" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-blue-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Dispatch Schedule</TabsTrigger>
              <TabsTrigger value="notes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-500 data-[state=active]:bg-purple-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Notes</TabsTrigger>
              <TabsTrigger value="edit-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-amber-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Edit Logs</TabsTrigger>
              <TabsTrigger value="event-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-500 data-[state=active]:bg-indigo-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Event Logs ({eventLogs.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="shipment-status" className="p-6">
              <StatusHistoryTab statusHistory={statusHistory} formData={formData} onChangeStatus={() => openStatusChangeDialog()} onDeleteEntry={handleDeleteStatusEntry} formatDateTime={formatDateTime} />
            </TabsContent>

            <TabsContent value="documents" className="p-6">
              <LoadDocumentsTab 
                documents={documents} selectedDocType={selectedDocType} setSelectedDocType={setSelectedDocType}
                fileInputRef={fileInputRef} uploadingDoc={uploadingDoc} onUpload={handleUploadDocument}
                onDownload={handleDownloadDocument} onDelete={handleDeleteDocument} isPreviewable={isPreviewable}
                formatDateTime={formatDateTime} previewDoc={previewDoc} previewUrl={previewUrl}
                previewLoading={previewLoading} handlePreviewHover={handlePreviewHover} handleOpenFullPreview={handleOpenFullPreview}
                onPullTicket={handlePullTicket} pullingTicket={pullingTicket}
                editingTypeId={editingTypeId} setEditingTypeId={setEditingTypeId}
                savingTypeId={savingTypeId} onChangeDocType={handleChangeDocType}
                onBulkChangeType={handleBulkChangeType}
              />
            </TabsContent>

            <TabsContent value="dispatch-schedule" className="p-6">
              <DispatchScheduleTab shipment={shipment} drivers={masterData.drivers} terminals={masterData.terminals} yards={masterData.yards} customers={customers} api={api} onDispatchCreated={() => toast.success('Dispatch created successfully')} />
            </TabsContent>

            <TabsContent value="notes" className="p-6 space-y-4">
              <Label className="text-slate-300">Remarks</Label>
              <Textarea value={formData.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)} placeholder="Additional notes..." className="bg-slate-950 border-slate-800 text-white min-h-[150px]" />
              {/* iter 264d — Billable-fees section available in Edit Load
                  under Notes so dispatchers can flag chargeable fees while
                  editing the load. Same component, same shipment ID, so
                  edits sync across every entry point (dashboard right-click
                  + here). */}
              {shipment?.id && (
                <div className="pt-2">
                  <ShipmentFeesSection shipmentId={shipment.id} />
                </div>
              )}
            </TabsContent>

            <TabsContent value="edit-logs" className="p-6">
              <p className="text-slate-400">Edit history will be shown here.</p>
            </TabsContent>

            <TabsContent value="event-logs" className="p-6">
              <EventLogsTab 
                eventLogs={eventLogs} 
                formatDateTime={formatDateTime}
                timelineEvents={timelineEvents}
                timelineLoading={timelineLoading}
                timelineError={timelineError}
              />
            </TabsContent>
          </Tabs>

          {activeTab === 'shipment-status' && (
            <div className="p-6 border-t border-slate-800 space-y-6">
              <LoadContainerInfoSection
                formData={formData} handleChange={handleChange} masterData={masterData}
                aiBadges={aiBadges} shipmentId={shipment?.id} api={api} onAIDismissed={handleAIDismissed}
              />
              
              {/* Terminal Availability Section - Auto-detects terminal */}
              <TerminalAvailabilitySection 
                shipmentId={shipment?.id}
                containerNumber={formData.container_number}
                terminalName={masterData.terminals?.find(t => t.id === formData.terminal_id)?.name}
                existingData={shipment?.availability_data || shipment?.apm_data}
                onDataUpdate={(data) => {
                  // Update form data with availability data
                  if (data?.lfd) handleChange('last_free_date', data.lfd);
                  if (data?.vessel_name && !formData.vessel_name) handleChange('vessel_name', data.vessel_name);
                  if (data?.holds) handleChange('holds', data.holds);
                }}
              />
              
              <LoadTerminalChassisSection
                formData={formData} handleChange={handleChange} masterData={masterData}
                aiBadges={aiBadges} shipmentId={shipment?.id} api={api} onAIDismissed={handleAIDismissed}
              />
              <LoadDatesSection
                formData={formData} handleChange={handleChange} masterData={masterData}
                aiBadges={aiBadges} shipmentId={shipment?.id} api={api} onAIDismissed={handleAIDismissed}
              />
              
              {/* Customer Selection - Edit-specific simplified version */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Customer & Delivery</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-slate-300">Customer</Label>
                    <Select value={formData.customer_id || ''} onValueChange={handleCustomerChange}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="edit-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id} className="text-white">
                            {customer.name} {customer.code ? `(${customer.code})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedCustomer && (
                    <div className="space-y-2 col-span-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-slate-300">Delivery Address</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-sky-300 hover:text-sky-200 hover:bg-sky-500/10"
                          onClick={() => setAddNewAddressOpen(true)}
                          data-testid="edit-address-add-new-btn"
                        >
                          + Add new address
                        </Button>
                      </div>
                      <Select value={formData.address_id || ''} onValueChange={handleAddressChange}>
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="edit-address-select">
                          <SelectValue placeholder="Select delivery address" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                          {selectedCustomer.addresses?.map(addr => (
                            <SelectItem key={addr.id} value={addr.id} className="text-white">
                              <div className="flex flex-col">
                                <span>{addr.label || addr.address}</span>
                                <span className="text-xs text-slate-400">{addr.city}, {addr.state} {addr.zip_code}</span>
                              </div>
                            </SelectItem>
                          ))}
                          {(!selectedCustomer.addresses || selectedCustomer.addresses.length === 0) && (
                            <div className="p-2 text-slate-400 text-sm">No addresses available — click &quot;+ Add new address&quot; above</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {formData.delivery_address && (
                    <div className="col-span-2 p-3 bg-slate-800/30 rounded-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 col-span-2">
                          <Label className="text-slate-400 text-xs">Address</Label>
                          <Input value={formData.delivery_address || ''} onChange={(e) => handleChange('delivery_address', e.target.value)} className="bg-slate-950 border-slate-800 text-white" placeholder="Street address" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-400 text-xs">City</Label>
                          <Input value={formData.delivery_city || ''} onChange={(e) => handleChange('delivery_city', e.target.value)} className="bg-slate-950 border-slate-800 text-white" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-400 text-xs">State/Zip</Label>
                          <div className="flex gap-2">
                            <Input value={formData.delivery_state || ''} onChange={(e) => handleChange('delivery_state', e.target.value)} placeholder="State" className="bg-slate-950 border-slate-800 text-white w-20" />
                            <Input value={formData.delivery_zip || ''} onChange={(e) => handleChange('delivery_zip', e.target.value)} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <LoadFeesSection formData={formData} handleChange={handleChange} />
              <LoadOptionsRemarksSection formData={formData} handleChange={handleChange} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Inline "Add new address" dialog — keeps dispatcher in flow */}
      <AddNewAddressDialog
        open={addNewAddressOpen}
        onOpenChange={setAddNewAddressOpen}
        selectedCustomer={selectedCustomer}
        newAddress={newAddress}
        setNewAddress={setNewAddress}
        onSave={handleSaveNewAddress}
        saving={savingNewAddress}
      />

      <StatusChangeDialog 
        open={statusDialogOpen} onOpenChange={setStatusDialogOpen}
        newStatus={newStatus} setNewStatus={setNewStatus}
        selectedDriverId={selectedDriverId} setSelectedDriverId={setSelectedDriverId}
        statusMessage={statusMessage} setStatusMessage={setStatusMessage}
        statusDate={statusDate} setStatusDate={setStatusDate}
        onConfirm={handleStatusChange} masterData={masterData} api={api}
      />

      <DeleteConfirmDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} shipment={shipment} deleting={deleting} onConfirm={handleDeleteLoad} />

      <FullPreviewModal open={showFullPreview} onOpenChange={setShowFullPreview} previewDoc={previewDoc} previewUrl={previewUrl} previewLoading={previewLoading} onDownload={handleDownloadDocument} />
    </>
  );
};

export default EditLoadSheet;
